import { ArrowLeftIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import ChecklistLayout from '@/pages/Settings/components/ChecklistSettings/ChecklistLayout';
import FigmaStepModal from '../../modal/FigmaStepModal';
import { ONBOARDING_STEP_LABELS, ONBOARDING_TOTAL_STEPS } from '../../../model/constants';

interface ScriptSetupStepProps {
  centerId: string;
  canContinue: boolean;
  isSaving: boolean;
  onBack: () => Promise<void>;
  onContinue: () => Promise<void>;
  onSaveAndExit: () => Promise<void>;
}

const ScriptSetupStep: React.FC<ScriptSetupStepProps> = ({
  centerId,
  canContinue,
  isSaving,
  onBack,
  onContinue,
  onSaveAndExit,
}) => {
  const primaryAction = (
    <button
      type="button"
      onClick={() => void onContinue()}
      disabled={!canContinue || isSaving}
      className="inline-flex items-center gap-3 rounded-full bg-gradient-to-l from-brand-500 to-brand-700 px-10 py-[14px] text-base font-bold text-white shadow-[0px_20px_25px_-5px_rgba(234,88,12,0.28),0px_8px_10px_-6px_rgba(234,88,12,0.22)] transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-70"
    >
      <span>{isSaving ? 'שומר...' : 'המשך לאימון AI'}</span>
      <ArrowLeftIcon className="h-4 w-4" />
    </button>
  );

  const secondaryAction = (
    <div className="flex items-center gap-2 sm:gap-4">
      <button
        type="button"
        onClick={() => void onSaveAndExit()}
        className="rounded-full px-5 py-[14px] text-base font-semibold text-text-800 transition hover:bg-neutral-100 dark:text-neutral-200 dark:hover:bg-neutral-700"
      >
        שמירה כטיוטה
      </button>
      <button
        type="button"
        onClick={() => void onBack()}
        className="inline-flex items-center gap-2 rounded-full px-5 py-[14px] text-base font-bold text-text-800 transition hover:bg-neutral-100 dark:text-neutral-200 dark:hover:bg-neutral-700"
      >
        <span>חזרה לשלב הקודם</span>
        <ArrowLeftIcon className="h-4 w-4 rotate-180" />
      </button>
    </div>
  );

  const headerAside = (
    <div className="flex justify-start">
      <div
        className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-bold ${
          canContinue
            ? 'border-success-200 bg-success-50 text-success-700 dark:border-success-800 dark:bg-success-950/40 dark:text-success-300'
            : 'border-brand-200 bg-brand-50 text-brand-700 dark:border-brand-800 dark:bg-brand-950/30 dark:text-brand-300'
        }`}
      >
        <CheckCircleIcon className="h-4 w-4" />
        {canContinue ? 'התסריט מוכן למעבר' : 'נדרש לפחות פריט אחד בכל קטגוריה'}
      </div>
    </div>
  );

  return (
    <FigmaStepModal
      embedded
      stepNumber={4}
      totalSteps={ONBOARDING_TOTAL_STEPS}
      stepLabels={[...ONBOARDING_STEP_LABELS]}
      titleBelow
      title="בניית תסריט המכירה המדויק"
      description="הגדירו את המבנה שיעזור ל-AI לנתח את השיחות שלכם בדיוק מקסימלי."
      showBadge={false}
      headerAside={headerAside}
      footerPrimary={primaryAction}
      footerSecondary={secondaryAction}
    >
      <div className="space-y-5">
        {!canContinue ? (
          <div className="rounded-xl border border-warning-200 bg-warning-50 px-4 py-3 text-right text-sm text-warning-800 dark:border-warning-800 dark:bg-warning-950/30 dark:text-warning-200">
            כדי להתקדם צריך לפחות שורה אחת בכל אחת מהקטגוריות: Hook, Key Questions, Qualification.
          </div>
        ) : null}

        <ChecklistLayout centerId={centerId} variant="onboarding" />
      </div>
    </FigmaStepModal>
  );
};

export default ScriptSetupStep;
